import { PelgoHeroBackgroundVideo } from '@/components/pelgo-hero-background-video'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useAuth } from '@/hooks/use-auth'
import { useIsMobile } from '@/hooks/use-is-mobile'
import { authService } from '@/services/auth.service'
import { userService } from '@/services/user.service'
import { useAuthStore } from '@/stores/auth.store'
import type { ApiError, OTPResponse } from '@/types'
import { zodResolver } from '@hookform/resolvers/zod'
import { ArrowLeft, Loader2, Mail, Smartphone } from 'lucide-react'
import type { ClipboardEvent, KeyboardEvent } from 'react'
import { useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { z } from 'zod'

// Get site type and redirect path
const site = import.meta.env.VITE_SITE || 'embrace'
const isConsole = site === 'console'
const postLoginPath = isConsole ? '/dashboard' : '/'

const AUTH_PRIMARY = '#1D72E8'

const embraceInputClass =
  'rounded-lg border-[#E5E7EB] bg-white text-center text-[#1A1A1A] placeholder:text-[#9CA3AF] focus-visible:border-[#1D72E8] focus-visible:ring-[#1D72E8]'

const embraceOtpInputClass =
  'h-10 w-10 shrink-0 rounded-lg border border-[#E5E7EB] bg-white p-0 text-center text-base font-semibold text-[#1A1A1A] focus-visible:border-[#1D72E8] focus-visible:ring-[#1D72E8] sm:h-11 sm:w-11 sm:text-lg lg:h-12 lg:w-12 lg:text-xl'

// Detect if input is email or phone
function detectIdentifierType(value: string): 'email' | 'phone' | 'unknown' {
  const trimmed = value.trim()
  if (trimmed.includes('@')) return 'email'
  const digitsOnly = trimmed.replace(/[\s\-\(\)\+]/g, '')
  if (digitsOnly.length >= 10 && /^\d+$/.test(digitsOnly)) return 'phone'
  return 'unknown'
}

const identifierSchema = z.object({
  identifier: z
    .string()
    .min(5, 'Please enter your email or phone number')
    .refine((val) => {
      const type = detectIdentifierType(val)
      if (type === 'email') {
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val)
      }
      if (type === 'phone') {
        const digits = val.replace(/[\s\-\(\)\+]/g, '')
        return digits.length >= 10
      }
      return false
    }, 'Please enter a valid email address or phone number'),
})

type IdentifierFormData = z.infer<typeof identifierSchema>

type LoginStep = 'identifier' | 'otp' | 'welcome'

const WELCOME_GOAL_OPTIONS = [
  {
    id: 'strategy',
    title: '1:1 Strategy',
    description:
      'Navigate your transition with a dedicated partner. We provide high-level, candid coaching to help you architect your next move.',
  },
  {
    id: 'skill-synthesis',
    title: 'Skill Synthesis',
    description:
      'The landscape is ever-changing. Learn about new workflows and emerging tools required to remain at the top of your field.',
  },
  {
    id: 'narrative-optimization',
    title: 'Narrative Optimization',
    description:
      "Everyone has a story and yours is amazing. We'll help you tell it to highlight how great it really is, helping you pass both the algorithmic filters and the human eye.",
  },
  {
    id: 'targeted-search',
    title: 'Targeted Search',
    description:
      'Learn precise networking strategies and data-driven search tactics to land your next role.',
  },
  {
    id: 'pivot',
    title: 'The Pivot',
    description:
      "Ready for a totally new path? Perhaps, starting a new business is something you've dreamed of. Or, getting a degree in a brand-new field has always intrigued you. Pelgo is your partner on this new journey.",
  },
] as const

export function LoginPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const nameFromUrl = searchParams.get('n')
  const isMobile = useIsMobile(1024)
  const { setAuth } = useAuthStore()
  const { isAuthenticated, isLoading: authLoading } = useAuth()
  const [step, setStep] = useState<LoginStep>('identifier')
  const [identifier, setIdentifier] = useState('')
  const [channel, setChannel] = useState<'email' | 'sms'>('email')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [otpDigits, setOtpDigits] = useState<string[]>(['', '', '', '', '', ''])
  const [otpAttempts, setOtpAttempts] = useState(0)
  const [shouldRefocusOtp, setShouldRefocusOtp] = useState(false)
  const [isPostAuthRouting, setIsPostAuthRouting] = useState(false)
  const otpInputRefs = useRef<(HTMLInputElement | null)[]>([])

  const identifierForm = useForm<IdentifierFormData>({
    resolver: zodResolver(identifierSchema),
  })

  const watchedIdentifier = identifierForm.watch('identifier') || ''
  const detectedType = detectIdentifierType(watchedIdentifier)

  useEffect(() => {
    if (isAuthenticated && !authLoading) {
      if (isPostAuthRouting) return
      if (!isConsole && !isMobile && step === 'welcome') {
        return
      }
      navigate(postLoginPath, { replace: true })
    }
  }, [isAuthenticated, authLoading, navigate, isMobile, step, isPostAuthRouting])

  useEffect(() => {
    if (step === 'otp') {
      setTimeout(() => otpInputRefs.current[0]?.focus(), 100)
    }
  }, [step])

  useEffect(() => {
    if (shouldRefocusOtp && !isLoading && step === 'otp') {
      otpInputRefs.current[0]?.focus()
      setShouldRefocusOtp(false)
    }
  }, [shouldRefocusOtp, isLoading, step])

  const onIdentifierSubmit = async (data: IdentifierFormData) => {
    setIsLoading(true)
    setError(null)

    try {
      const response: OTPResponse = await authService.requestOTP(data.identifier, isConsole)
      setIdentifier(data.identifier)
      setChannel(response.channel)
      setStep('otp')
    } catch (err) {
      const apiError = err as ApiError
      setError(apiError.message || 'Failed to send verification code')
    } finally {
      setIsLoading(false)
    }
  }

  const verifyOTP = async (code: string) => {
    setIsLoading(true)
    setError(null)
    setIsPostAuthRouting(true)

    try {
      const response = await authService.verifyOTP(identifier, code)
      setAuth(response.user, response.access_token)
      if (!isConsole && !isMobile) {
        const sessionState = await userService.getSessionState()
        if (!sessionState.has_resume) {
          setStep('welcome')
          return
        }
        navigate(postLoginPath, { replace: true })
        return
      }
      navigate(postLoginPath, { replace: true })
    } catch (err) {
      const apiError = err as ApiError
      const newAttempts = otpAttempts + 1
      setOtpAttempts(newAttempts)

      if (newAttempts >= 2) {
        setError('Too many failed attempts. Please request a new code.')
      } else {
        setError(apiError.message || 'Invalid code. You have 1 attempt remaining.')
      }

      setOtpDigits(['', '', '', '', '', ''])
      setShouldRefocusOtp(true)
    } finally {
      setIsPostAuthRouting(false)
      setIsLoading(false)
    }
  }

  const handleOtpChange = (index: number, value: string) => {
    const digitsOnly = value.replace(/\D/g, '')

    if (digitsOnly.length > 1) {
      const newDigits = [...otpDigits]
      for (let i = 0; i < digitsOnly.length && i < 6; i++) {
        newDigits[i] = digitsOnly.charAt(i)
      }
      setOtpDigits(newDigits)

      if (digitsOnly.length >= 6) {
        verifyOTP(digitsOnly.slice(0, 6))
      } else {
        otpInputRefs.current[digitsOnly.length]?.focus()
      }
      return
    }

    if (digitsOnly.length === 0 && value.length > 0) return

    const newDigits = [...otpDigits]
    newDigits[index] = digitsOnly
    setOtpDigits(newDigits)

    if (digitsOnly && index < 5) {
      otpInputRefs.current[index + 1]?.focus()
    }

    if (digitsOnly && index === 5) {
      const code = newDigits.join('')
      if (code.length === 6) {
        verifyOTP(code)
      }
    }
  }

  const handleOtpKeyDown = (index: number, e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !otpDigits[index] && index > 0) {
      otpInputRefs.current[index - 1]?.focus()
    }
  }

  const handleOtpPaste = (e: ClipboardEvent, index = 0) => {
    e.preventDefault()
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6)
    if (pastedData.length === 0) return

    const startIndex = pastedData.length === 6 ? 0 : index
    const newDigits = [...otpDigits]

    for (let i = 0; i < pastedData.length && startIndex + i < 6; i++) {
      newDigits[startIndex + i] = pastedData.charAt(i)
    }

    setOtpDigits(newDigits)

    const fullCode = newDigits.join('')
    if (fullCode.length === 6 && !newDigits.includes('')) {
      verifyOTP(fullCode)
    } else {
      const nextEmptyIndex = newDigits.findIndex((d) => d === '')
      const focusIndex =
        nextEmptyIndex !== -1 ? nextEmptyIndex : Math.min(startIndex + pastedData.length, 5)
      otpInputRefs.current[focusIndex]?.focus()
    }
  }

  const handleBack = () => {
    setStep('identifier')
    setError(null)
    setOtpDigits(['', '', '', '', '', ''])
    setOtpAttempts(0)
  }

  const handleResendCode = async () => {
    setIsLoading(true)
    setError(null)

    try {
      await authService.requestOTP(identifier, isConsole)
      setOtpDigits(['', '', '', '', '', ''])
      setOtpAttempts(0)
      setShouldRefocusOtp(true)
    } catch (err) {
      const apiError = err as ApiError
      setError(apiError.message || 'Failed to resend code')
    } finally {
      setIsLoading(false)
    }
  }

  const getInputHint = () => {
    if (!watchedIdentifier || watchedIdentifier.length < 3) {
      return 'Enter your email or phone number'
    }
    if (detectedType === 'email') {
      return "We'll send a code to this email"
    }
    if (detectedType === 'phone') {
      return "We'll text you a code"
    }
    return 'Enter a valid email or phone number'
  }

  const getIcon = () => {
    if (detectedType === 'email') return <Mail className="h-8 w-8 text-white" />
    if (detectedType === 'phone') return <Smartphone className="h-8 w-8 text-white" />
    return <Mail className="h-8 w-8 text-white" />
  }

  const getOtpMessage = () => {
    return `We sent a 6-digit code to ${identifier}`
  }

  const themeColors = isConsole
    ? {
        gradient: 'from-slate-50/40 via-background to-background dark:from-slate-950/15',
        iconBg: 'from-slate-600 to-slate-700',
        iconShadow: 'shadow-slate-500/30',
        cardShadow: 'shadow-slate-500/10',
        inputBorder: 'border-slate-200 dark:border-slate-700 focus-visible:ring-slate-500',
        button:
          'from-slate-600 to-slate-700 hover:from-slate-700 hover:to-slate-800 shadow-slate-500/25',
        hoverBg: 'hover:bg-slate-50 dark:hover:bg-slate-950/20',
      }
    : {
        gradient: 'from-blue-50/40 via-background to-background dark:from-blue-950/15',
        iconBg: 'from-blue-500 to-blue-600',
        iconShadow: 'shadow-blue-500/30',
        cardShadow: 'shadow-blue-500/10',
        inputBorder: 'border-blue-200 dark:border-blue-800 focus-visible:ring-blue-500',
        button:
          'from-blue-600 to-blue-600 hover:from-blue-700 hover:to-blue-700 shadow-blue-500/25',
        hoverBg: 'hover:bg-blue-50 dark:hover:bg-blue-950/20',
      }

  const getTitle = () => {
    if (step === 'welcome') {
      return "Places We'll Go. Together."
    }
    if (step === 'otp') {
      return channel === 'email' ? 'Check Your Email' : 'Check Your Phone'
    }
    if (isConsole) return 'Admin Console'
    return nameFromUrl ? `Welcome back, ${nameFromUrl}` : 'Welcome Back'
  }

  const getDescription = () => {
    if (step === 'otp') return getOtpMessage()
    return isConsole
      ? 'Sign in to access the admin dashboard'
      : 'Sign in to continue your career journey'
  }

  // After OTP, useAuth refetches /me and sets isLoading true; do not swap the whole page for that
  // or the hero video unmounts and restarts.
  if (authLoading && !isAuthenticated) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="animate-pulse text-lg">Loading...</div>
      </div>
    )
  }

  if (isConsole) {
    return (
      <div className="min-h-screen">
        <div
          className={`absolute inset-0 -z-10 bg-gradient-to-b ${themeColors.gradient} dark:via-background dark:to-background`}
        />

        <div className="container flex min-h-[calc(100vh-4rem)] items-center justify-center py-10">
          <Card
            className={`w-full max-w-md border-0 shadow-2xl animate-slide-up ${themeColors.cardShadow}`}
          >
            <CardHeader className="space-y-4 pb-8">
              <div className="flex justify-center">
                <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-slate-500 dark:bg-slate-800 dark:text-slate-400">
                  Admin
                </span>
              </div>
              <div className="flex items-center justify-center">
                <div
                  className={`flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br ${themeColors.iconBg} shadow-lg ${themeColors.iconShadow}`}
                >
                  {step === 'otp' ? (
                    channel === 'email' ? (
                      <Mail className="h-8 w-8 text-white" />
                    ) : (
                      <Smartphone className="h-8 w-8 text-white" />
                    )
                  ) : (
                    getIcon()
                  )}
                </div>
              </div>
              <CardTitle className="text-center text-3xl">{getTitle()}</CardTitle>
              <CardDescription className="text-center text-base leading-relaxed">
                {getDescription()}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {step === 'identifier' ? (
                <form
                  key="identifier-form"
                  onSubmit={identifierForm.handleSubmit(onIdentifierSubmit)}
                  className="space-y-4 animate-slide-up"
                >
                  <div className="space-y-2">
                    <Label htmlFor="identifier">Email or Phone Number</Label>
                    <Input
                      id="identifier"
                      type="text"
                      placeholder="you@example.com or +1234567890"
                      {...identifierForm.register('identifier')}
                      aria-invalid={identifierForm.formState.errors.identifier ? 'true' : 'false'}
                      disabled={isLoading}
                      className={`text-center ${themeColors.inputBorder}`}
                    />
                    {identifierForm.formState.errors.identifier && (
                      <p className="text-sm text-destructive">
                        {identifierForm.formState.errors.identifier.message}
                      </p>
                    )}
                    <p className="text-xs text-muted-foreground">{getInputHint()}</p>
                  </div>

                  {error && (
                    <div className="rounded-md bg-destructive/10 p-3">
                      <p className="text-sm text-destructive">{error}</p>
                    </div>
                  )}

                  <Button
                    type="submit"
                    className={`w-full rounded-full bg-gradient-to-r ${themeColors.button} shadow-lg`}
                    disabled={isLoading}
                  >
                    {isLoading ? 'Sending code...' : 'Send verification code'}
                  </Button>
                </form>
              ) : (
                <div key="otp-form" className="space-y-6 animate-slide-up">
                  <div className="space-y-3">
                    <Label className="block text-center">Verification Code</Label>
                    <div className="flex justify-center gap-2">
                      {otpDigits.map((digit, index) => (
                        <Input
                          // biome-ignore lint/suspicious/noArrayIndexKey: OTP array is fixed size and order never changes
                          key={index}
                          ref={(el) => {
                            otpInputRefs.current[index] = el
                          }}
                          type="text"
                          inputMode="numeric"
                          autoComplete={index === 0 ? 'one-time-code' : 'off'}
                          value={digit}
                          onChange={(e) => handleOtpChange(index, e.target.value)}
                          onKeyDown={(e) => handleOtpKeyDown(index, e)}
                          onPaste={(e) => handleOtpPaste(e, index)}
                          disabled={isLoading || otpAttempts >= 2}
                          className={`h-14 w-12 text-center text-2xl font-semibold ${themeColors.inputBorder}`}
                        />
                      ))}
                    </div>
                    {isLoading && (
                      <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Verifying...
                      </div>
                    )}
                  </div>

                  {error && (
                    <div className="rounded-md bg-destructive/10 p-3">
                      <p className="text-center text-sm text-destructive">{error}</p>
                    </div>
                  )}

                  {otpAttempts >= 2 && (
                    <p className="text-center text-sm text-muted-foreground">
                      Didn&apos;t receive a code? Check your spam folder, or if you were invited
                      through your employer, reach out to their HR team to confirm your access.
                    </p>
                  )}

                  <div className="flex flex-col gap-2 pt-2">
                    <Button
                      type="button"
                      variant="ghost"
                      className={`w-full ${themeColors.hoverBg}`}
                      onClick={handleResendCode}
                      disabled={isLoading}
                    >
                      Resend code
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      className={`w-full ${themeColors.inputBorder} ${themeColors.hoverBg}`}
                      onClick={handleBack}
                      disabled={isLoading}
                    >
                      <ArrowLeft className="mr-2 h-4 w-4" />
                      Use different email or phone
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    )
  }

  return (
    <div className="relative isolate min-h-dvh overflow-hidden bg-[#F3F4F6] font-auth">
      <div className="pointer-events-none absolute inset-0 -z-20 min-h-dvh">
        <PelgoHeroBackgroundVideo />
      </div>
      {/* Same light gray as the old right column so type and card match the previous design */}
      <div className="pointer-events-none absolute inset-0 -z-10 bg-[#F3F4F6]/88" aria-hidden />

      <div className="absolute left-6 top-6 z-20 sm:left-10 sm:top-8">
        <img
          src="/logomark-white.png"
          alt="Pelgo"
          className="h-9 w-auto max-w-[200px] object-contain object-left sm:h-10"
        />
      </div>

      <div className="relative z-10 flex min-h-dvh items-center justify-center px-6 pb-10 pt-24 sm:px-10 sm:pt-28">
        <div
          className={`w-full rounded-3xl bg-white p-8 shadow-[0_8px_30px_rgb(0,0,0,0.08)] sm:p-10 animate-slide-up ${step === 'welcome' ? 'max-w-2xl' : 'max-w-md'}`}
        >
          <div className="mb-8 space-y-2 text-center">
            <h2
              className={`font-bold text-[#1A1A1A] ${step === 'welcome' ? 'text-xl' : 'text-2xl'}`}
            >
              {getTitle()}
            </h2>
            {step !== 'welcome' ? (
              <p className="text-base leading-relaxed text-[#6B7280]">{getDescription()}</p>
            ) : null}
          </div>

          {step === 'identifier' ? (
            <form
              key="identifier-form"
              onSubmit={identifierForm.handleSubmit(onIdentifierSubmit)}
              className="space-y-5 animate-slide-up"
            >
              <div className="space-y-2 text-center">
                <Label htmlFor="identifier" className="block text-sm font-normal text-[#6B7280]">
                  Enter your email or phone number
                </Label>
                <Input
                  id="identifier"
                  type="text"
                  placeholder="you@example.com or +1234567890"
                  {...identifierForm.register('identifier')}
                  aria-invalid={identifierForm.formState.errors.identifier ? 'true' : 'false'}
                  disabled={isLoading}
                  className={embraceInputClass}
                />
                {identifierForm.formState.errors.identifier && (
                  <p className="text-center text-sm text-destructive">
                    {identifierForm.formState.errors.identifier.message}
                  </p>
                )}
              </div>

              {error && (
                <div className="rounded-md bg-destructive/10 p-3">
                  <p className="text-center text-sm text-destructive">{error}</p>
                </div>
              )}

              <Button
                type="submit"
                className="h-11 w-full rounded-lg border-0 font-semibold text-white shadow-sm hover:opacity-95"
                style={{ backgroundColor: AUTH_PRIMARY }}
                disabled={isLoading}
              >
                {isLoading ? 'Sending code...' : 'Send Verification Code'}
              </Button>
            </form>
          ) : step === 'otp' ? (
            <div key="otp-form" className="space-y-6 animate-slide-up">
              <div className="space-y-3 text-center">
                <Label htmlFor="otp-0" className="block text-sm font-normal text-[#6B7280]">
                  Verification Code
                </Label>
                <div className="flex flex-nowrap justify-center gap-1.5 sm:gap-2">
                  {otpDigits.map((digit, index) => (
                    <Input
                      // biome-ignore lint/suspicious/noArrayIndexKey: OTP array is fixed size and order never changes
                      key={index}
                      id={index === 0 ? 'otp-0' : undefined}
                      ref={(el) => {
                        otpInputRefs.current[index] = el
                      }}
                      type="text"
                      inputMode="numeric"
                      autoComplete={index === 0 ? 'one-time-code' : 'off'}
                      value={digit}
                      onChange={(e) => handleOtpChange(index, e.target.value)}
                      onKeyDown={(e) => handleOtpKeyDown(index, e)}
                      onPaste={(e) => handleOtpPaste(e, index)}
                      disabled={isLoading || otpAttempts >= 2}
                      className={embraceOtpInputClass}
                    />
                  ))}
                </div>
                {isLoading && (
                  <div className="flex items-center justify-center gap-2 text-sm text-[#6B7280]">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Verifying...
                  </div>
                )}
              </div>

              {error && (
                <div className="rounded-md bg-destructive/10 p-3">
                  <p className="text-center text-sm text-destructive">{error}</p>
                </div>
              )}

              {otpAttempts >= 2 && (
                <p className="text-center text-sm text-[#6B7280]">
                  Didn&apos;t receive a code? Check your spam folder, or if you were invited through
                  your employer, reach out to their HR team to confirm your access.
                </p>
              )}

              <div className="flex flex-col items-center gap-4 pt-2">
                <button
                  type="button"
                  className="text-sm font-semibold hover:underline disabled:pointer-events-none disabled:opacity-50"
                  style={{ color: AUTH_PRIMARY }}
                  onClick={handleResendCode}
                  disabled={isLoading}
                >
                  Resend Code
                </button>
                <Button
                  type="button"
                  variant="outline"
                  className="h-11 w-full rounded-lg border-[#E5E7EB] bg-white font-medium text-[#1A1A1A] hover:bg-[#F9FAFB]"
                  onClick={handleBack}
                  disabled={isLoading}
                >
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Use a different email or phone
                </Button>
              </div>
            </div>
          ) : (
            <div key="welcome-step" className="space-y-6 animate-slide-up">
              <div className="space-y-0">
                {WELCOME_GOAL_OPTIONS.map((option) => (
                  <div
                    key={option.id}
                    className="border-t border-neutral-200/90 py-4 text-left first:border-t-0 first:pt-0"
                  >
                    <h3 className="text-xs font-semibold text-[#1A1A1A]">{option.title}</h3>
                    <p className="mt-1 text-xs leading-relaxed text-[#6B7280]">
                      {option.description}
                    </p>
                  </div>
                ))}
              </div>

              <Button
                type="button"
                className="h-11 w-full rounded-lg border-0 font-semibold text-white shadow-sm hover:opacity-95"
                style={{ backgroundColor: AUTH_PRIMARY }}
                onClick={() => navigate('/resume', { state: { fromWelcome: true } })}
              >
                Continue
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
